import { Component, lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { supportsWebGL } from "../../lib/webgl-support";

const TableBackdrop = lazy(() =>
  import("./TableBackdrop").then((module) => ({ default: module.TableBackdrop })),
);

/** Repli permanent : c'est lui qu'on voit avant, pendant et après un échec. */
const StaticBackdrop = () => <div className="table-backdrop table-backdrop--static" aria-hidden="true" />;

/**
 * Le module du fond ne se charge jamais tout seul en cas d'échec réseau : sans
 * ce filet, un `lazy` qui n'aboutit pas remonterait jusqu'à la racine et
 * laisserait la page blanche. Un fond décoratif ne peut pas coûter la partie.
 */
class BackdropBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

const idle = (run: () => void): (() => void) => {
  if (typeof requestIdleCallback === "function") {
    const handle = requestIdleCallback(run, { timeout: 2_000 });
    return () => cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(run, 400);
  return () => window.clearTimeout(handle);
};

/**
 * Décide s'il faut faire venir le fond animé, et quand.
 *
 * Le module pèse à lui seul plus que tout le reste de l'application ; l'importer
 * pendant le démarrage revient à disputer le processeur au premier écran, sur
 * les téléphones qui en ont le moins. Il attend donc le premier moment creux —
 * et ne vient pas du tout pour qui a demandé moins d'animations ou n'a pas de
 * WebGL, deux cas où il ne rendrait qu'un `div`.
 *
 * Le fond statique reste peint dessous en permanence : la bascule ne montre
 * aucune image vide, les deux rendus ayant les mêmes teintes.
 */
export const BackdropGate = () => {
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION);

    // La préférence se règle en cours de route sur les deux plateformes : on la
    // suit plutôt que de la figer, comme le fait le reste de l'application.
    const onChange = () => setLoad(query.matches ? false : supportsWebGL());
    const cancel = idle(onChange);
    query.addEventListener("change", onChange);

    return () => {
      cancel();
      query.removeEventListener("change", onChange);
    };
  }, []);

  return (
    <>
      <StaticBackdrop />
      {load && (
        <BackdropBoundary>
          <Suspense fallback={null}>
            <TableBackdrop />
          </Suspense>
        </BackdropBoundary>
      )}
    </>
  );
};
